import {defineMessages, MessageDescriptor} from 'react-intl';
import type {TopicMap} from './TopicSelector';

type TopicMessagesKeys =
  | 'apiIntegrationLabel'
  | 'atlasLabel'
  | 'billingLabel'
  | 'capitalLabel'
  | 'connectLabel'
  | 'corporateCardLabel'
  | 'declineLabel'
  | 'disputesLabel'
  | 'financialReportsLabel'
  | 'issuingLabel'
  | 'loginIssuesLabel'
  | 'migrationsLabel'
  | 'paymentApisLabel'
  | 'payoutsLabel'
  | 'privacyRequestLabel'
  | 'radarLabel'
  | 'refundsLabel'
  | 'sigmaLabel'
  | 'stripeAppsLabel'
  | 'taxLabel'
  | 'terminalLabel'
  | 'thirdPartyIntegrationsLabel'
  | 'treasuryLabel'
  | 'verificationLabel'
  | 'embeddedLabel'
  | 'otherLabel'
  | 'apmsLabel'
  | 'changeAccountInfoLabel'
  | 'chargeLookupLabel'
  | 'gettingStartedLabel'
  | 'pciLabel'
  | 'pricingLabel';

export const topicMessages: Record<TopicMessagesKeys, MessageDescriptor> =
  defineMessages({
    apiIntegrationLabel: {
      id: 'support_conversations_client.topicLabel.api_integration',
      description:
        'Label displayed for the "api_integration" topic in the contact topic selector UI',
      defaultMessage: 'API Integration',
    },
    atlasLabel: {
      id: 'support_conversations_client.topicLabel.atlas',
      description:
        'Label displayed for the "atlas" topic in the contact topic selector UI',
      defaultMessage: 'Atlas',
    },
    billingLabel: {
      id: 'support_conversations_client.topicLabel.billing',
      description:
        'Label displayed for the "billing" topic in the contact topic selector UI',
      defaultMessage: 'Subscriptions & Invoices',
    },
    capitalLabel: {
      id: 'support_conversations_client.topicLabel.capital',
      description:
        'Label displayed for the "capital" topic in the contact topic selector UI',
      defaultMessage: 'Capital',
    },
    connectLabel: {
      id: 'support_conversations_client.topicLabel.connect',
      description:
        'Label displayed for the "connect" topic in the contact topic selector UI',
      defaultMessage: 'Connect',
    },
    corporateCardLabel: {
      id: 'support_conversations_client.topicLabel.corporateCard',
      description:
        'Label displayed for the "corporate_card" topic in the contact topic selector UI',
      defaultMessage: 'Corporate Card',
    },
    declineLabel: {
      id: 'support_conversations_client.topicLabel.decline',
      description:
        'Label displayed for the "decline" topic in the contact topic selector UI',
      defaultMessage: 'Declines',
    },
    disputesLabel: {
      id: 'support_conversations_client.topicLabel.disputes',
      description:
        'Label displayed for the "disputes" topic in the contact topic selector UI',
      defaultMessage: 'Disputes',
    },
    financialReportsLabel: {
      id: 'support_conversations_client.topicLabel.financial_reports',
      description:
        'Label displayed for the "financial_reports" topic in the contact topic selector UI',
      defaultMessage: 'Financial Reports',
    },
    issuingLabel: {
      id: 'support_conversations_client.topicLabel.issuing',
      description:
        'Label displayed for the "issuing" topic in the contact topic selector UI',
      defaultMessage: 'Issuing',
    },
    loginIssuesLabel: {
      id: 'support_conversations_client.topicLabel.loginIssues',
      description:
        'Label displayed for the "login_issues" topic in the contact topic selector UI',
      defaultMessage: 'Login Issues',
    },
    migrationsLabel: {
      id: 'support_conversations_client.topicLabel.migrations',
      description:
        'Label displayed for the "migrations" topic in the contact topic selector UI',
      defaultMessage: 'Data Migrations',
    },
    paymentApisLabel: {
      id: 'support_conversations_client.topicLabel.payment_apis',
      description:
        'Label displayed for the "payment_apis" topic in the contact topic selector UI',
      defaultMessage: 'Payment APIs',
    },
    payoutsLabel: {
      id: 'support_conversations_client.topicLabel.payouts',
      description:
        'Label displayed for the "payouts" topic in the contact topic selector UI',
      defaultMessage: 'Payouts',
    },
    privacyRequestLabel: {
      id: 'support_conversations_client.topicLabel.privacyRequest',
      description:
        'Label displayed for the "privacy_request" topic in the contact topic selector UI',
      defaultMessage: 'Privacy Request',
    },
    radarLabel: {
      id: 'support_conversations_client.topicLabel.radar',
      description:
        'Label displayed for the "radar" topic in the contact topic selector UI',
      defaultMessage: 'Radar',
    },
    refundsLabel: {
      id: 'support_conversations_client.topicLabel.refunds',
      description:
        'Label displayed for the "refunds" topic in the contact topic selector UI',
      defaultMessage: 'Refunds',
    },
    sigmaLabel: {
      id: 'support_conversations_client.topicLabel.sigma',
      description:
        'Label displayed for the "sigma" topic in the contact topic selector UI',
      defaultMessage: 'Sigma',
    },
    stripeAppsLabel: {
      id: 'support_conversations_client.topicLabel.stripeApps',
      description:
        'Label displayed for the "stripe apps" topic in the contact topic selector UI',
      defaultMessage: 'Stripe Apps',
    },
    taxLabel: {
      id: 'support_conversations_client.topicLabel.tax',
      description:
        'Label displayed for the "tax" topic in the contact topic selector UI',
      defaultMessage: 'Tax',
    },
    terminalLabel: {
      id: 'support_conversations_client.topicLabel.terminal',
      description:
        'Label displayed for the "terminal" topic in the contact topic selector UI',
      defaultMessage: 'Terminal',
    },
    thirdPartyIntegrationsLabel: {
      id: 'support_conversations_client.topicLabel.thirdPartyIntegrations',
      description:
        'Label displayed for the "third_party_integrations" topic in the contact topic selector UI',
      defaultMessage: 'Third-Party Integrations',
    },
    treasuryLabel: {
      id: 'support_conversations_client.topicLabel.treasury',
      description:
        'Label displayed for the "treasury" topic in the contact topic selector UI',
      defaultMessage: 'Treasury',
    },
    verificationLabel: {
      id: 'support_conversations_client.topicLabel.verification',
      description:
        'Label displayed for the "verification" topic in the contact topic selector UI',
      defaultMessage: 'Verification',
    },
    otherLabel: {
      id: 'support_conversations_client.topicLabel.other',
      description:
        'Label displayed for the "other" topic in the contact topic selector UI',
      defaultMessage: 'Other',
    },
    // below topics shouldn't ever be displayed
    embeddedLabel: {
      id: 'support_conversations_client.support.topicLabel.embedded',
      description:
        'Label displayed for the "embedded" topic in the contact topic selector UI',
      defaultMessage: 'Embedded',
    },
    apmsLabel: {
      id: 'support_conversations_client.topicLabel.apms',
      description:
        'Label displayed for the "apms" topic in the contact topic selector UI',
      defaultMessage: 'APMs',
    },
    changeAccountInfoLabel: {
      id: 'support_conversations_client.topicLabel.changeAccountInfo',
      description:
        'Label displayed for the "change_account_info" topic in the contact topic selector UI',
      defaultMessage: 'Change Account Info',
    },
    chargeLookupLabel: {
      id: 'support_conversations_client.topicLabel.chargeLookup',
      description:
        'Label displayed for the "charge_lookup" topic in the contact topic selector UI',
      defaultMessage: 'Charge Lookup',
    },
    gettingStartedLabel: {
      id: 'support_conversations_client.topicLabel.gettingStarted',
      description:
        'Label displayed for the "getting_started" topic in the contact topic selector UI',
      defaultMessage: 'Getting Started',
    },
    pciLabel: {
      id: 'support_conversations_client.topicLabel.pci',
      description:
        'Label displayed for the "pci" topic in the contact topic selector UI',
      defaultMessage: 'PCI',
    },
    pricingLabel: {
      id: 'support_conversations_client.topicLabel.pricing',
      description:
        'Label displayed for the "pricing" topic in the contact topic selector UI',
      defaultMessage: 'Pricing',
    },
  });

export const topicValueToMessages: TopicMap = {
  api_integration: {
    label: topicMessages.apiIntegrationLabel,
  },
  atlas: {
    label: topicMessages.atlasLabel,
  },
  billing: {
    label: topicMessages.billingLabel,
  },
  capital: {
    label: topicMessages.capitalLabel,
  },
  connect: {
    label: topicMessages.connectLabel,
  },
  corporate_card: {
    label: topicMessages.corporateCardLabel,
  },
  declines: {
    label: topicMessages.declineLabel,
  },
  disputes: {
    label: topicMessages.disputesLabel,
  },
  financial_reports: {
    label: topicMessages.financialReportsLabel,
  },
  issuing: {
    label: topicMessages.issuingLabel,
  },
  login_issues: {
    label: topicMessages.loginIssuesLabel,
  },
  migrations: {
    label: topicMessages.migrationsLabel,
  },
  payment_apis: {
    label: topicMessages.paymentApisLabel,
  },
  payouts: {
    label: topicMessages.payoutsLabel,
  },
  privacy_request: {
    label: topicMessages.privacyRequestLabel,
  },
  radar: {
    label: topicMessages.radarLabel,
  },
  refunds: {
    label: topicMessages.refundsLabel,
  },
  sigma: {
    label: topicMessages.sigmaLabel,
  },
  stripe_apps: {
    label: topicMessages.stripeAppsLabel,
  },
  tax: {
    label: topicMessages.taxLabel,
  },
  terminal: {
    label: topicMessages.terminalLabel,
  },
  third_party_integrations: {
    label: topicMessages.thirdPartyIntegrationsLabel,
  },
  treasury: {
    label: topicMessages.treasuryLabel,
  },
  verification: {
    label: topicMessages.verificationLabel,
  },
  embedded: {
    label: topicMessages.embeddedLabel,
  },
  other: {
    label: topicMessages.otherLabel,
  },
  apms: {
    label: topicMessages.apmsLabel,
  },
  change_account_info: {
    label: topicMessages.changeAccountInfoLabel,
  },
  charge_lookup: {
    label: topicMessages.chargeLookupLabel,
  },
  getting_started: {
    label: topicMessages.gettingStartedLabel,
  },
  pci: {
    label: topicMessages.pciLabel,
  },
  pricing: {
    label: topicMessages.pricingLabel,
  },
};
